import { useMemo } from "react"

import { CollectionTableColumn } from "~/constants/collections"
import { PurchaseOrderTableColumn } from "~/constants/purchaseOrderTable"
import { api } from "~/utils/api"
import { SalesMetricColumns, TrimMetricsColumns } from "~/utils/demandDictionary"
import { useFlags } from "~/features/feature-flags"
import { ProductTableColumn } from "~/utils/productTableUtils"

import type { ColumnNamingId } from "./columnUtils"
import { BuySpecificColumn } from "~/constants/buyTable"

const COLUMN_FLAGS = [
  "budget",
  "unfulfillablePreorders",
  "demandPlan",
  "demandPlanTrim",
  "noosSupplyPlan",
  "sizeDimensions",
] as const
export type ColumnFlag = (typeof COLUMN_FLAGS)[number]

export interface ColumnAvailabilityContext {
  flags: Record<ColumnFlag, boolean>
  returnsEnabled: boolean
  orgId?: string
  hasAvailability?: boolean
  isCollectionScoped?: boolean
  incomingHasCorrectCollectionId?: boolean
}

// Month cover metrics are restricted to a single customer for now
const MONTH_COVER_ORG_IDS = ["hummel"]

interface ColumnGate {
  id: ColumnNamingId
  flag?: ColumnFlag
  when?: (ctx: ColumnAvailabilityContext) => boolean
}

export const COLUMN_GATES: ColumnGate[] = [
  { id: ProductTableColumn.PreOrderFulfillment, flag: "unfulfillablePreorders" },
  { id: ProductTableColumn.UnfulfillablePreorder, flag: "unfulfillablePreorders" },
  { id: ProductTableColumn.AtRiskPreorder, flag: "unfulfillablePreorders" },
  { id: PurchaseOrderTableColumn.UnfulfillableBeforeEAD, flag: "unfulfillablePreorders" },
  { id: PurchaseOrderTableColumn.AtRiskBeforeEAD, flag: "unfulfillablePreorders" },
  { id: CollectionTableColumn.Budget, flag: "budget" },
  { id: ProductTableColumn.SizeDimension1, flag: "sizeDimensions" },
  { id: ProductTableColumn.SizeDimension2, flag: "sizeDimensions" },
  { id: ProductTableColumn.ExpectedReturns, when: (c) => c.returnsEnabled },
  { id: ProductTableColumn.AvailableToSell, when: (c) => Boolean(c.hasAvailability) },
  // Without the "Explicit order-collection mapping" setting the column is empty in collection scope.
  {
    id: ProductTableColumn.IncomingStock,
    when: (c) => !c.isCollectionScoped || Boolean(c.incomingHasCorrectCollectionId),
  },
  ...[
    PurchaseOrderTableColumn.MonthCover,
    PurchaseOrderTableColumn.MonthCoverWithPO,
    PurchaseOrderTableColumn.MonthCoverWithPOMinusLeadtime,
  ].map((id) => ({
    id,
    when: (c: ColumnAvailabilityContext) =>
      Boolean(c.orgId && MONTH_COVER_ORG_IDS.includes(c.orgId)),
  })),
  ...Object.values(SalesMetricColumns).map((id) => ({ id, flag: "demandPlan" as ColumnFlag })),
  ...Object.values(TrimMetricsColumns).map((id) => ({ id, flag: "demandPlanTrim" as ColumnFlag })),
  ...Object.values(BuySpecificColumn).map((id) => ({ id, flag: "noosSupplyPlan" as ColumnFlag })),
]

const gateById = new Map(COLUMN_GATES.map((gate) => [gate.id, gate]))

export function isColumnAvailable(id: ColumnNamingId, ctx: ColumnAvailabilityContext): boolean {
  const gate = gateById.get(id)
  if (!gate) return true
  if (gate.flag) return ctx.flags[gate.flag]
  return gate.when?.(ctx) ?? true
}

function useGatingFlags(): Record<ColumnFlag, boolean> {
  const flags = useFlags()
  const signature = COLUMN_FLAGS.map((key) => (flags[key] ? "1" : "0")).join("")
  return useMemo(
    () =>
      Object.fromEntries(COLUMN_FLAGS.map((key, i) => [key, signature[i] === "1"])) as Record<
        ColumnFlag,
        boolean
      >,
    [signature],
  )
}

export function useColumnAvailability(options?: { isCollectionScoped?: boolean }): {
  hiddenColumnIds: ColumnNamingId[]
} {
  const flags = useGatingFlags()
  const { data: company } = api.company.getCompany.useQuery()
  const { data: hasAvailability } = api.sku.hasAvailability.useQuery()
  const isCollectionScoped = options?.isCollectionScoped ?? false
  const returnsEnabled = Boolean(company?.returnsSettings?.enabled)
  // Default true while company loads so the column isn't hidden before settings arrive.
  const incomingHasCorrectCollectionId = company?.incomingHasCorrectCollectionId ?? true

  const hiddenColumnIds = useMemo(() => {
    const ctx: ColumnAvailabilityContext = {
      flags,
      returnsEnabled,
      orgId: company?.orgId,
      hasAvailability,
      isCollectionScoped,
      incomingHasCorrectCollectionId,
    }
    return COLUMN_GATES.map((gate) => gate.id).filter((id) => !isColumnAvailable(id, ctx))
  }, [
    flags,
    returnsEnabled,
    company?.orgId,
    hasAvailability,
    isCollectionScoped,
    incomingHasCorrectCollectionId,
  ])

  return { hiddenColumnIds }
}
